import {Header} from "@mantine/core";
import {useDisclosure} from "@mantine/hooks";
import {spotlight} from "@mantine/spotlight";
import {
  BanknotesIcon,
  BeakerIcon,
  BellAlertIcon,
  BookOpenIcon,
  ChartBarIcon,
  ChatBubbleLeftEllipsisIcon,
  InboxArrowDownIcon,
  MapIcon,
  PresentationChartLineIcon,
  QuestionMarkCircleIcon,
  RectangleStackIcon,
  ScaleIcon,
  SparklesIcon,
  TruckIcon,
  UserIcon,
  ViewColumnsIcon,
} from "@heroicons/react/16/solid";
import {skipToken} from "@reduxjs/toolkit/query";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";
import {
  changeRegionAndResetData,
  getCurrentRegion,
  RegionEntity,
  transportCategory,
  useGetContainerLevelSimulationQuery,
  useGetNoticeBadgeSummaryQuery,
  useGetPersonQuery
} from "@ots-frontend/ots-data";
import {getFormattedDateTime, useAccessibleRegions, useAuth, useChat} from "@ots-frontend/ots-ui";
import {
  FeedbackPrompt,
  ConnectedNoticeDrawer,
  OtsAppNavbar,
  OtsAppLogo,
  otsAppLogos,
  OtsAppNavbarItem,
  OtsNavLink,
  OtsRegionSwitcher,
  OtsStatusStripItem,
  OtsTransportCategorySwitcher,
  useOtsAppearanceMenuItems
} from "@ots-frontend/ots-ui-tw";
import {useNavIslandCounts} from "../../hooks/useNavIslandCounts";
import {useNavLayout} from "../../hooks/useNavLayout";
import {loadBaseData, logout} from "../../store/auth/actions";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {useAppThunkDispatch} from "../../store/hooks/useAppThunkDispatch";
import {setAppCategory} from "../../store/ui/actions";
import {getAppCategory} from "../../store/ui/selectors";

const CATEGORIES = [transportCategory.water, transportCategory.oil];

/**
 * HubGo's navigation across the top, the same bar DispatchGo and the dashboard have: the region switcher,
 * destinations, processes, search, notices, and the account menu, over a status strip.
 */
export const TopNavBar = () => {
  const {t} = useTranslation(["translation", "common"]);
  const appearance = useOtsAppearanceMenuItems();
  const [, setNavLayout] = useNavLayout();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const thunkDispatch = useAppThunkDispatch();
  const chat = useChat();
  const {userId, hasAccess} = useAuth();
  const {data: person} = useGetPersonQuery(userId ?? skipToken);
  const appCategory = useAppSelector(getAppCategory);
  const currentRegion = useAppSelector<RegionEntity | undefined>(getCurrentRegion);
  const {regions, canSwitchRegion} = useAccessibleRegions();
  const [noticesOpened, notices] = useDisclosure(false);

  const canViewNotices = hasAccess("notice_view");
  const {data: noticeBadge} = useGetNoticeBadgeSummaryQuery(canViewNotices ? undefined : skipToken);

  const canViewSettlement = hasAccess("settlement_view");
  const canViewFinancial = hasAccess("invoice_view");
  // The side navigation's ten destinations, six across the top: field and accounting each open a flyout.
  const items = useMemo<OtsAppNavbarItem[]>(() => {
    const to = (path: string) => () => navigate(path);
    const accounting: OtsNavLink[] = [
      ...(canViewSettlement ? [{id: "/settlement", label: t<string>("app.navbar.item.settlement", "Settlement"), description: "Settle volumes with companies", icon: <ScaleIcon/>, onClick: to("/settlement")}] : []),
      ...(canViewFinancial ? [{id: "/financial", label: t<string>("app.navbar.item.financial", "Financial"), description: "Invoices and ticket comparisons", icon: <BanknotesIcon/>, onClick: to("/financial")}] : []),
    ];
    return [
      {id: "/map", label: t<string>("app.navbar.item.map", "Map"), icon: <MapIcon/>, onClick: to("/map")},
      {id: "/requests", label: t<string>("app.navbar.item.dispatchrequests", "Dispatch Requests"), icon: <InboxArrowDownIcon/>, onClick: to("/requests")},
      {id: "field", label: "Field", icon: <TruckIcon/>, children: [
        {id: "/container-status", label: t<string>("app.navbar.item.containerstatus", "Container Status"), description: "Tank levels and shut-ins", icon: <RectangleStackIcon/>, onClick: to("/container-status")},
        {id: "/trucking", label: t<string>("app.navbar.item.trucking", "Trucking"), description: "Tickets hauled by truck", icon: <TruckIcon/>, onClick: to("/trucking")},
        {id: "/pipelines", label: t<string>("app.navbar.item.pipeline", "Pipeline"), description: "Flows, meters, and graphs", icon: <ChartBarIcon/>, onClick: to("/pipelines")},
        {id: "/ops", label: t<string>("app.navbar.item.ops", "Ops"), description: "Day-to-day operations", icon: <SparklesIcon/>, onClick: to("/ops")},
      ]},
      ...(accounting.length > 0 ? [{id: "accounting", label: "Accounting", icon: <BanknotesIcon/>, children: accounting}] : []),
      {id: "/insights", label: t<string>("app.navbar.item.insights", "Insights"), icon: <PresentationChartLineIcon/>, onClick: to("/insights")},
      {id: "/directory", label: t<string>("app.navbar.item.directory", "Directory"), icon: <BookOpenIcon/>, onClick: to("/directory")},
    ];
  }, [navigate, t, canViewSettlement, canViewFinancial]);
  // The longest matching path wins, so a page under a destination marks that destination.
  const activePath = items.flatMap(item => item.children?.map(child => child.id) ?? [item.id])
    .filter(path => pathname === path || pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];

  const onRegionChange = useCallback((region: RegionEntity | undefined) => {
    if (region) thunkDispatch(changeRegionAndResetData(region.id, loadBaseData));
  }, [thunkDispatch]);

  const onLogout = useCallback(() => {
    thunkDispatch(logout());
  }, [thunkDispatch]);

  // The side layout's header island, as chips in the status strip.
  const {unauthorizedDispatchRequestCount, availableLoads, potentialShutInsCount} = useNavIslandCounts();
  const {data: simulation, isError: simulationFailed} = useGetContainerLevelSimulationQuery(undefined, {pollingInterval: 5000});
  const statusItems = useMemo<OtsStatusStripItem[]>(() => [
    ...(!simulationFailed && simulation?.enabled ? [{
      id: "simulation",
      label: "Simulated Container Levels On",
      icon: <BeakerIcon/>,
      tone: "warning" as const,
      onClick: () => navigate("/directory"),
    }] : []),
    ...(unauthorizedDispatchRequestCount > 0 ? [{
      id: "unauthorized-requests",
      label: `${unauthorizedDispatchRequestCount} Unauthorized Dispatch Request${unauthorizedDispatchRequestCount === 1 ? "" : "s"}`,
      icon: <InboxArrowDownIcon/>,
      onClick: () => navigate("/requests/unauthorized"),
    }] : []),
    ...(availableLoads > 0 ? [{
      id: "available-loads",
      label: `${availableLoads} Available Load${availableLoads === 1 ? "" : "s"}`,
      icon: <TruckIcon/>,
      onClick: () => navigate("/container-status"),
    }] : []),
    ...(potentialShutInsCount > 0 ? [{
      id: "potential-shut-ins",
      label: `${potentialShutInsCount} Potential Shut-In${potentialShutInsCount === 1 ? "" : "s"}`,
      icon: <BellAlertIcon/>,
      tone: "danger" as const,
      tooltip: t<string>("common.entity.container.potentialshutin.desc", "Potential Shut-Ins are containers that are set to 'Do Not Dispatch' but whose fill or time to fill is critically high."),
      onClick: () => navigate("/container-status/shut-ins"),
    }] : []),
  ], [simulation?.enabled, simulationFailed, unauthorizedDispatchRequestCount, availableLoads, potentialShutInsCount, navigate, t]);

  const userName = person ? `${person.firstName} ${person.lastName}`.trim() : undefined;

  // The Mantine shell pads the page by the header's height, so it follows the bar's real height.
  const [height, setHeight] = useState(94);

  return (
    <>
      <Header height={height} p={0} withBorder={false} styles={{root: {backgroundColor: "transparent"}}}>
        <OtsAppNavbar
          brand="hub-go"
          colorScheme="system"
                    onHeightChange={setHeight}
                    pinKey="hub-go"
          logo={(
            <OtsAppLogo {...otsAppLogos["hub-go"]} onClick={() => navigate("/")}/>
          )}
          context={(
            <OtsTransportCategorySwitcher
              compact
              value={appCategory.code}
              options={CATEGORIES.map(category => ({code: category.code, label: category.displayValue.defaultMessage}))}
              onChange={(code) => {
                const next = CATEGORIES.find(category => category.code === code);
                if (next) dispatch(setAppCategory(next));
              }}/>
          )}
          start={currentRegion && (
            <OtsRegionSwitcher
              compact
              currentRegion={currentRegion}
              regions={regions}
              canSwitchRegion={canSwitchRegion}
              onRegionChange={onRegionChange}
              onConfigureClick={hasAccess("region_edit")
                ? () => navigate(`/regions/profile?id=${currentRegion.id}`)
                : undefined}
              variant="navbar"/>
          )}
          items={items}
          currentId={activePath}
          processes={{}}
          onSearch={() => spotlight.open()}
          notices={canViewNotices ? {count: noticeBadge?.activeCount, onClick: notices.open} : undefined}
          actions={chat.enabled ? [{id: "chat", label: "Chat", icon: <ChatBubbleLeftEllipsisIcon/>, badgeCount: chat.unread, onClick: () => navigate("/chat")}] : []}
          user={userName ? {name: userName, details: person?.emailAddress ? [person.emailAddress] : undefined} : undefined}
          userMenuItems={[
            {id: "profile", label: "My profile", icon: <UserIcon/>, onClick: () => navigate("/profile")},
            {id: "help", label: "Help & Support", icon: <QuestionMarkCircleIcon/>, onClick: () => navigate("/help")},
            ...appearance,
            {id: "layout", label: "Use side navigation", icon: <ViewColumnsIcon/>, onClick: () => setNavLayout("side"), separated: true},
          ]}
          end={<FeedbackPrompt placement="inline" launcherSize={32} brand="hub-go" colorScheme="system"/>}
          onSignOut={onLogout}
          status={{items: statusItems}}/>
      </Header>
      <ConnectedNoticeDrawer
        open={noticesOpened}
        onClose={notices.close}
        formatDateTime={getFormattedDateTime}
        brand="hub-go"
        colorScheme="system"/>
    </>
  );
};
